import React from "react";
import "./Home.css";

export default function Home() {
  return (
    <div className="cn-home">
      <section className="cn-hero">
        <div className="cn-hero-grid">
          <div className="cn-hero-copy">
            <div className="cn-eyebrow">Conscious Neurons LLC</div>

            <h1 className="cn-title">
              Intelligent software, product architecture, and AI systems built
              with clarity.
            </h1>

            <p className="cn-lead">
              Conscious Neurons is an AI-first software company focused on
              building systems that feel structured, explainable, and deeply
              useful. We design digital products where interface, logic,
              cognition, and infrastructure work together instead of fighting
              each other.
            </p>

            <div className="cn-hero-points">
              <span>AI-first engineering</span>
              <span>Explainable architecture</span>
              <span>Product-grade execution</span>
            </div>
          </div>

          <div className="cn-hero-panel">
            <div className="cn-panel-shell">
              <div className="cn-panel-orb cn-panel-orb-1" />
              <div className="cn-panel-orb cn-panel-orb-2" />
              <div className="cn-panel-content">
                <div className="cn-panel-chip">Systems Thinking</div>
                <h3>From cognition to product delivery</h3>
                <p>
                  We approach software as a layered system: user interface,
                  state, intelligence, backend, and operational flow. That is
                  the foundation behind the tools and products we are building.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cn-section">
        <div className="cn-section-head">
          <div className="cn-section-tag">About the company</div>
          <h2>Why Conscious Neurons exists</h2>
          <p>
            Our work begins with a simple idea: intelligent systems should not
            feel opaque or chaotic. They should be understandable, aligned, and
            designed around real human use. We build with a strong respect for
            structure, transparency, and long-term product thinking.
          </p>
        </div>

        <div className="cn-grid cn-grid-3">
          <article className="cn-card">
            <h3>AI with interpretability</h3>
            <p>
              We care about systems that can be reasoned about. That means clear
              design, explainable behavior, and software that people can trust
              in practice.
            </p>
          </article>

          <article className="cn-card">
            <h3>Engineering with intent</h3>
            <p>
              We do not separate architecture from product quality. Clean
              implementation, thoughtful UI, and durable systems are part of the
              same process.
            </p>
          </article>

          <article className="cn-card">
            <h3>Products with direction</h3>
            <p>
              Our products are built to solve real workflow problems, not to
              simply demonstrate technology in isolation.
            </p>
          </article>
        </div>
      </section>

      <section className="cn-section">
        <div className="cn-section-head">
          <div className="cn-section-tag">Flagship ecosystem</div>
          <h2>The Nexa and Cloud Weaver product path</h2>
          <p>
            Conscious Neurons is building a connected product ecosystem centered
            around modern app generation and backend automation. These products
            are not presented here as services. They are part of our product
            philosophy and show how we think about developer experience from
            frontend structure to backend generation.
          </p>
        </div>

        <div className="cn-grid cn-grid-2">
          <article className="cn-card cn-card-feature">
            <div className="cn-card-badge">Frontend Platform</div>
            <h3>Nexa</h3>
            <p>
              Nexa is focused on frontend generation and developer structure. It
              brings together React, Vite, and Angular-style productivity ideas
              to create applications quickly with strong layout, routing, and UI
              organization from the start.
            </p>
            <ul>
              <li>Prebuilt application structure</li>
              <li>Responsive navigation patterns</li>
              <li>Cleaner UI and scalable component layout</li>
              <li>Fast project startup through CLI workflows</li>
            </ul>
          </article>

          <article className="cn-card cn-card-feature">
            <div className="cn-card-badge cn-card-badge-gold">
              Backend Platform
            </div>
            <h3>Cloud Weaver</h3>
            <p>
              Cloud Weaver is the backend generation layer that follows the
              frontend workflow. It is designed to help developers move into AWS
              infrastructure with less friction, turning an application into a
              fuller product system.
            </p>
            <ul>
              <li>DynamoDB entry workflow</li>
              <li>Lambda CRUD generation</li>
              <li>API Gateway and authorizers</li>
              <li>Testing flow with generated endpoint commands</li>
            </ul>
          </article>
        </div>

        <div className="cn-product-bridge">
          <div className="cn-bridge-line" />
          <p>
            <strong>Nexa</strong> shapes the frontend.{" "}
            <strong>Cloud Weaver</strong> extends it into backend generation.
            Together they represent how Conscious Neurons thinks about modern
            product systems: clear, layered, and connected.
          </p>
        </div>
      </section>

      <section className="cn-section">
        <div className="cn-section-head">
          <div className="cn-section-tag">Philosophy</div>
          <h2>Technology should amplify awareness, not complexity</h2>
          <p>
            We believe AI and software should help people think more clearly,
            move faster, and understand their systems better. That is why we
            care about interpretability, good interfaces, and architecture that
            supports growth instead of creating confusion.
          </p>
        </div>

        <div className="cn-grid cn-grid-2">
          <article className="cn-card">
            <h3>Human-centered intelligence</h3>
            <p>
              We design software that respects human attention and reduces
              unnecessary friction. The goal is not noise. The goal is cognitive
              support and practical utility.
            </p>
          </article>

          <article className="cn-card">
            <h3>Full-cycle product thinking</h3>
            <p>
              From research and architecture to interface design and deployment,
              our perspective stays product-driven. Every layer should support
              the next one cleanly.
            </p>
          </article>
        </div>
      </section>
    </div>
  );
}
